import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

interface Props {
  onScan: (text: string) => void;
  onClose: () => void;
}

/** 카메라로 QR 을 읽어 텍스트를 반환하는 전체화면 모달. jsQR 사용. */
export default function QrScannerModal({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    let stream: MediaStream | null = null;
    const canvas = document.createElement("canvas");

    const start = async () => {
      try {
        // 비보안 컨텍스트(HTTP)거나 WebView 가 mediaDevices 를 노출하지 않으면 아예 undefined 다.
        if (!navigator.mediaDevices?.getUserMedia) {
          setError(
            "이 앱/브라우저에서 카메라를 쓸 수 없어요.\n(보안 연결(HTTPS) 또는 앱 카메라 권한 설정이 필요해요)",
          );
          return;
        }
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" },
          });
        } catch (envErr) {
          // 일부 기기/웹뷰는 facingMode 제약을 거부한다 — 제약 없이 재시도.
          const name = (envErr as { name?: string })?.name ?? "";
          if (name === "OverconstrainedError" || name === "NotFoundError") {
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
          } else {
            throw envErr;
          }
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();

        const tick = () => {
          if (cancelled) return;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(img.data, img.width, img.height, {
                inversionAttempts: "dontInvert",
              });
              if (code && code.data) {
                onScanRef.current(code.data);
                return;
              }
            }
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        // 실제 원인을 로그로 남겨 진단 가능하게 한다(예전엔 삼켜서 무조건 같은 문구만 떴음).
        const name = (e as { name?: string })?.name ?? "";
        console.error("[QrScanner] getUserMedia 실패:", name, e);
        if (name === "NotAllowedError" || name === "SecurityError") {
          setError(
            "카메라 권한이 거부됐어요.\n앱/브라우저 설정에서 카메라 권한을 허용한 뒤 다시 시도해 주세요.",
          );
        } else if (
          name === "NotFoundError" ||
          name === "OverconstrainedError"
        ) {
          setError("사용할 수 있는 카메라를 찾지 못했어요.");
        } else if (name === "NotReadableError" || name === "TrackStartError") {
          setError(
            "다른 앱이 카메라를 사용 중이에요. 카메라 앱을 종료한 뒤 다시 시도해 주세요.",
          );
        } else {
          setError(
            `카메라를 열 수 없어요. (${name || "알 수 없는 오류"})\n앱 카메라 권한을 확인해 주세요.`,
          );
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black/90">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-[15px] font-semibold">MY코드 QR 스캔</span>
        <button
          type="button"
          onClick={onClose}
          className="text-[14px] text-white/80"
        >
          닫기
        </button>
      </div>
      <div className="flex flex-1 items-center justify-center px-4">
        {error ? (
          <div className="whitespace-pre-line px-6 text-center text-[14px] leading-relaxed text-white">
            {error}
          </div>
        ) : (
          <div className="relative w-full max-w-[420px]">
            <video
              ref={videoRef}
              playsInline
              muted
              className="max-h-[70vh] w-full rounded-xl bg-black object-cover"
            />
            <div className="pointer-events-none absolute inset-6 rounded-2xl border-2 border-[#FF9934]" />
          </div>
        )}
      </div>
      <p className="pb-8 pt-3 text-center text-[12px] text-white/70">
        고객의 <b className="text-white">MY코드 QR</b>을 사각형 안에 비춰주세요.
      </p>
    </div>
  );
}
